import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Col, Divider, Input, Modal, Progress, Row, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, EyeOutlined } from '@ant-design/icons';
import {
  CLEANUP_FIELD_LABELS,
  CLEANUP_FIELDS,
  DEFAULT_KEEP_WORDS,
  cleanName,
  cleanupFieldOptionsSchema,
  type CleanupCase,
  type CleanupField,
  type CleanupFieldOptions,
  type NameCleanupChange,
  type NameCleanupInput,
  type NameCleanupPreview,
} from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { useApplyNameCleanup, usePreviewNameCleanup, type ApplyProgress } from '../../features/cleanup/api';
import { plural } from '../../utils/format';

const CASE_OPTIONS: { value: CleanupCase; label: string }[] = [
  { value: 'title', label: 'Capitalize each word (Elena Ioannou)' },
  { value: 'sentence', label: 'Capitalize the first word (Elena ioannou)' },
  { value: 'upper', label: 'ALL CAPITALS (ELENA IOANNOU)' },
  { value: 'lower', label: 'all lowercase (elena ioannou)' },
  { value: 'none', label: "Don't change capitals, only spaces" },
];

const EXAMPLES: Record<CleanupField, string> = {
  firstName: 'ELENA  MARIA',
  lastName: "O'BRIEN-IOANNOU",
  companyName: 'BANK OF CYPRUS PLC',
};

const FIELD_HINTS: Record<CleanupField, string> = {
  firstName: 'Contacts',
  lastName: 'Contacts',
  companyName: 'Organizations',
};

const initialFields = (): Record<CleanupField, CleanupFieldOptions> => ({
  firstName: cleanupFieldOptionsSchema.parse({ enabled: true }),
  lastName: cleanupFieldOptionsSchema.parse({ enabled: true }),
  companyName: cleanupFieldOptionsSchema.parse({ enabled: true, lowerSmallWords: true }),
});

function FieldOptions({ field, value, onChange, keepWords }: { field: CleanupField; value: CleanupFieldOptions; onChange: (v: CleanupFieldOptions) => void; keepWords: string[] }) {
  const [example, setExample] = useState(EXAMPLES[field]);
  const set = (patch: Partial<CleanupFieldOptions>) => onChange({ ...value, ...patch });
  const result = cleanName(example, field, value, keepWords);
  const titleCase = value.case === 'title';
  return (
    <div>
      <Space align="center" size={12} style={{ marginBottom: value.enabled ? 16 : 0 }}>
        <Switch checked={value.enabled} onChange={(enabled) => set({ enabled })} aria-label={`Clean up ${CLEANUP_FIELD_LABELS[field]}`} />
        <Typography.Text strong style={{ fontSize: 15 }}>
          {CLEANUP_FIELD_LABELS[field]}
        </Typography.Text>
        <Tag variant="filled">{FIELD_HINTS[field]}</Tag>
      </Space>
      {value.enabled && (
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <Space orientation="vertical" size={10} style={{ width: '100%' }}>
              <Select value={value.case} onChange={(c) => set({ case: c })} options={CASE_OPTIONS} style={{ width: '100%' }} aria-label="Capitalization" />
              <Checkbox checked={value.onlyUniform} disabled={value.case === 'none'} onChange={(e) => set({ onlyUniform: e.target.checked })}>
                Only change values written in ALL CAPITALS or all lowercase
                <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  Leaves names that already mix capitals alone, such as McDonald or eBay.
                </Typography.Text>
              </Checkbox>
              <Checkbox checked={value.fixSpaces} onChange={(e) => set({ fixSpaces: e.target.checked })}>
                Remove extra spaces
              </Checkbox>
              {field === 'companyName' ? (
                <Checkbox checked={value.lowerSmallWords} disabled={!titleCase} onChange={(e) => set({ lowerSmallWords: e.target.checked })}>
                  Keep small words lowercase (of, and, the, for…)
                </Checkbox>
              ) : (
                <Checkbox checked={value.lowerParticles} disabled={!titleCase} onChange={(e) => set({ lowerParticles: e.target.checked })}>
                  Keep name particles lowercase (van, de, von, al…)
                </Checkbox>
              )}
            </Space>
          </Col>
          <Col xs={24} lg={12}>
            <div className="cleanup-example">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Try it
              </Typography.Text>
              <Input value={example} onChange={(e) => setExample(e.target.value)} aria-label={`Example ${CLEANUP_FIELD_LABELS[field]}`} />
              <div className="cleanup-example-result">
                <ArrowRightOutlined style={{ color: '#9CA3AF' }} />
                <Typography.Text strong style={{ whiteSpace: 'pre-wrap' }}>{result || ' '}</Typography.Text>
                {result === example && <Typography.Text type="secondary">(no change)</Typography.Text>}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}

export function NameCleanupPage() {
  const { message } = App.useApp();
  const [fields, setFields] = useState(initialFields);
  const [keepWords, setKeepWords] = useState<string[]>(DEFAULT_KEEP_WORDS);
  const [preview, setPreview] = useState<NameCleanupPreview | null>(null);
  const [progress, setProgress] = useState<ApplyProgress | null>(null);
  const [done, setDone] = useState<{ contacts: number; organizations: number } | null>(null);
  const previewMut = usePreviewNameCleanup();
  const applyMut = useApplyNameCleanup();

  const input = useMemo<NameCleanupInput>(() => ({ fields, keepWords }), [fields, keepWords]);
  const anyEnabled = CLEANUP_FIELDS.some((f) => fields[f].enabled);
  const totalChanges = preview ? CLEANUP_FIELDS.reduce((n, f) => n + preview.changes[f], 0) : 0;

  // any option change makes the preview stale
  const changeField = (f: CleanupField, v: CleanupFieldOptions) => {
    setFields((prev) => ({ ...prev, [f]: v }));
    setPreview(null);
    setDone(null);
  };
  const changeKeepWords = (words: string[]) => {
    setKeepWords(words);
    setPreview(null);
    setDone(null);
  };

  const runPreview = async () => {
    setDone(null);
    try {
      setPreview(await previewMut.mutateAsync(input));
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const runApply = () => {
    if (!preview) return;
    Modal.confirm({
      title: `Clean up ${plural(totalChanges, 'value')}?`,
      content: 'The names are rewritten in place. This cannot be undone automatically.',
      okText: 'Clean up',
      onOk: async () => {
        setProgress({ target: 'contacts', scanned: 0, updated: 0 });
        try {
          const totals = await applyMut.mutateAsync({ input, onProgress: setProgress });
          setDone(totals);
          setPreview(null);
          message.success('Names cleaned up');
        } catch (e) {
          message.error(errorMessage(e));
        } finally {
          setProgress(null);
        }
      },
    });
  };

  const progressPercent = (() => {
    if (!progress || !preview) return 0;
    const total = preview.records.contacts + preview.records.organizations;
    const read = progress.target === 'contacts' ? progress.scanned : preview.records.contacts + progress.scanned;
    return total ? Math.min(99, Math.round((read / total) * 100)) : 0;
  })();

  return (
    <div className="page page-narrow">
      <PageHeader title="Name clean up" subtitle="Fix capitals and spacing in names across the CRM, for example ELENA IOANNOU becomes Elena Ioannou." />
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>
        <Card title={<CardTitle title="What to clean up" subtitle="Choose the fields and how each one should be written" />}>
          {CLEANUP_FIELDS.map((f, i) => (
            <div key={f}>
              {i > 0 && <Divider style={{ margin: '20px 0' }} />}
              <FieldOptions field={f} value={fields[f]} onChange={(v) => changeField(f, v)} keepWords={keepWords} />
            </div>
          ))}
        </Card>

        <Card
          title={<CardTitle title="Words to keep as written" subtitle="Abbreviations and legal suffixes keep exactly this spelling, e.g. GmbH, PLC, UK" />}
          extra={<Button type="link" onClick={() => changeKeepWords(DEFAULT_KEEP_WORDS)}>Reset</Button>}
        >
          <Select
            mode="tags"
            value={keepWords}
            onChange={changeKeepWords}
            tokenSeparators={[',', ' ']}
            open={false}
            suffixIcon={null}
            placeholder="Type a word and press Enter"
            style={{ width: '100%' }}
            aria-label="Words to keep as written"
          />
        </Card>

        <Card
          title={<CardTitle title="Review and apply" subtitle="See what would change before anything is saved" />}
          extra={
            <Space>
              <Button icon={<EyeOutlined />} onClick={runPreview} loading={previewMut.isPending} disabled={!anyEnabled || applyMut.isPending}>
                {preview ? 'Refresh preview' : 'Preview changes'}
              </Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={runApply} disabled={!preview || !totalChanges} loading={applyMut.isPending}>
                Apply changes
              </Button>
            </Space>
          }
        >
          {progress ? (
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Typography.Text>
                Cleaning {progress.target}… {plural(progress.updated, 'record')} updated so far
              </Typography.Text>
              <Progress percent={progressPercent} status="active" />
            </Space>
          ) : done ? (
            <Alert
              type="success"
              showIcon
              title="Clean up finished"
              description={`${plural(done.contacts, 'contact')} and ${plural(done.organizations, 'organization')} updated.`}
            />
          ) : !anyEnabled ? (
            <Typography.Text type="secondary">Turn on at least one field above.</Typography.Text>
          ) : !preview ? (
            <Typography.Text type="secondary">Preview the changes to see how many names would be fixed.</Typography.Text>
          ) : (
            <PreviewResult preview={preview} fields={fields} />
          )}
        </Card>
      </Space>
    </div>
  );
}

function PreviewResult({ preview, fields }: { preview: NameCleanupPreview; fields: Record<CleanupField, CleanupFieldOptions> }) {
  const enabled = CLEANUP_FIELDS.filter((f) => fields[f].enabled);
  const total = enabled.reduce((n, f) => n + preview.changes[f], 0);
  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        {enabled.map((f) => (
          <Col key={f} xs={24} sm={8}>
            <div className="cleanup-stat">
              <Typography.Text type="secondary">{CLEANUP_FIELD_LABELS[f]}</Typography.Text>
              <div className="cleanup-stat-value">{preview.changes[f].toLocaleString()}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                of {plural(preview.checked[f], 'value')} would change
              </Typography.Text>
            </div>
          </Col>
        ))}
      </Row>
      {total === 0 ? (
        <Alert type="info" showIcon title="Nothing to clean up" description="Every name already matches the chosen format." />
      ) : (
        <Table<NameCleanupChange>
          size="small"
          rowKey={(r) => `${r.id}:${r.field}`}
          dataSource={preview.samples}
          pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          footer={total > preview.samples.length ? () => <Typography.Text type="secondary">Showing the first {preview.samples.length} of {total.toLocaleString()} changes.</Typography.Text> : undefined}
          columns={[
            { title: 'Field', dataIndex: 'field', width: 130, render: (f: CleanupField) => CLEANUP_FIELD_LABELS[f] },
            { title: 'Before', dataIndex: 'before', render: (v: string) => <Typography.Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>{v}</Typography.Text> },
            { title: 'After', dataIndex: 'after', render: (v: string) => <Typography.Text strong>{v}</Typography.Text> },
          ]}
        />
      )}
    </Space>
  );
}
